import React, { useState, useEffect } from "react";
import { Outlet, useLocation, Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import { getAuth } from "firebase/auth";
import useUnreadMessagesCount from '../../hooks/useUnreadMessagesCount'; // ✅ AJOUT DU HOOK
import { useUnreadNotificationsCount } from '../../hooks/useUnreadNotificationsCount'; // ✅ AJOUT DU HOOK NOTIFICATIONS
// ✅ NOUVEAU : Import du hook pour démarrer le scheduler automatiquement
import { useNotificationScheduler } from '../../hooks/useNotificationScheduler';
// ✅ NOUVEAU : Import du hook pour messages équipe
import useUnreadTeamMessages from '../../hooks/useUnreadTeamMessages';

// Ajout progressif des imports - ÉTAPE 1 : React + Firebase
const SuperAdminDashboard = () => {
  const location = useLocation();
  const auth = getAuth();
  
  // ✅ AJOUT DU HOOK POUR COMPTER LES MESSAGES
  const { count: messagesCount } = useUnreadMessagesCount(auth.currentUser?.uid, auth.currentUser?.email);
  
  // ✅ AJOUT DU HOOK POUR COMPTER LES NOTIFICATIONS TIMELINE
  const { count: notificationsCount } = useUnreadNotificationsCount();

  // ✅ NOUVEAU : Hook pour démarrer automatiquement le scheduler de retards
  const { isRunning: schedulerRunning, forceCheck } = useNotificationScheduler();
  
  // ✅ NOUVEAU : Hook pour messages équipe non lus
  const { count: teamUnreadCount } = useUnreadTeamMessages();
  
  // États pour les données dashboard
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalDossiers: 0,
    dossiersActifs: 0,
    dossiersRetard: 0,
    totalClients: 0,
    tauxProgression: 0
  });
  
  const [sourcesContact, setSourcesContact] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  // Charger les données dashboard seulement sur la page principale
  useEffect(() => {
    if (location.pathname === '/admin' || location.pathname === '/admin/') {
      loadDashboardData();
    }
  }, [location.pathname]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Charger les dossiers
      const dossiersSnap = await getDocs(collection(db, 'dossiers'));
      const dossiers = dossiersSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Charger les vrais clients
      const usersSnap = await getDocs(query(
        collection(db, 'users'),
        where('role', '==', 'client')
      ));

      const realClients = usersSnap.docs.filter(doc => {
        const userData = doc.data();
        const role = userData.role;
        return role === 'client' && 
               role !== 'admin' && 
               role !== 'superadmin' && 
               role !== 'charge-affaire' && 
               role !== 'projectmanager';
      });

      // ✅ NOUVEAU : Compter les dossiers en retard selon le nouveau système
      const dossiersEnRetard = dossiers.filter(d => d.status === 'en retard').length;
      
      // Calculer les statistiques (avec nouveau système de retards)
      const totalDossiers = dossiers.length;
      const dossiersActifs = dossiers.filter(d => 
        d.progressGlobal < 100 && !d.archived && d.status !== 'en retard'
      ).length;
      
      // Utiliser le nouveau système de retards
      const dossiersRetard = dossiersEnRetard;

      const totalClients = realClients.length;
      
      const moyenneProgression = dossiers.length > 0 
        ? dossiers.reduce((sum, d) => sum + (d.progressGlobal || 0), 0) / dossiers.length
        : 0;

      setStats({
        totalDossiers,
        dossiersActifs,
        dossiersRetard,
        totalClients,
        tauxProgression: Math.round(moyenneProgression)
      });

      // Analyser les sources de contact
      const sourcesCount = {};
      const sourcesLabels = {
        'site web': 'Site web',
        'réseaux sociaux': 'Réseaux sociaux', 
        'recommandation': 'Recommandation',
        'salon/événement': 'Salon / Événement',
        'publicité': 'Publicité',
        'partenariat': 'Partenariat',
        'prospection': 'Prospection directe',
        'autre': 'Autre'
      };

      dossiers.forEach(dossier => {
        const source = dossier.sourceContact || 'autre';
        sourcesCount[source] = (sourcesCount[source] || 0) + 1;
      });

      const chartColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'];
      
      const sourcesData = Object.entries(sourcesCount).map(([key, value], index) => ({
        name: sourcesLabels[key] || key,
        value,
        color: chartColors[index % chartColors.length],
        percentage: Math.round((value / totalDossiers) * 100)
      }));

      setSourcesContact(sourcesData);

      // Activité récente
      const recentDossiers = dossiers
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return dateB - dateA;
        })
        .slice(0, 5)
        .map(dossier => ({
          id: dossier.id,
          title: `Dossier: ${dossier.clientName || dossier.nomClient || 'Client Inconnu'}`,
          description: `Progression: ${dossier.progressGlobal || 0}%`,
          date: dossier.createdAt?.toDate?.() || new Date(dossier.createdAt || Date.now()),
          status: dossier.progressGlobal >= 100 ? 'completed' : 
                 dossier.progressGlobal > 0 ? 'in-progress' : 'pending'
        }));

      setRecentActivity(recentDossiers);

    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Contenu du dashboard principal
  const renderDashboardContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    return (
      <div className="max-w-7xl mx-auto">
        {/* Header avec indicateur scheduler */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 text-gray-900">Tableau de Bord</h1>
              <p className="text-gray-600">Vue d'ensemble de votre activité</p>
            </div>
            {/* ✅ NOUVEAU : Indicateur du scheduler de retards */}
            {schedulerRunning && (
              <div className="flex items-center space-x-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-700 font-medium">
                  Surveillance des retards active
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Dossiers */}
          <div className="rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 cursor-pointer bg-white hover:bg-gray-50"
               onClick={() => window.location.href = '/admin/dossiers'}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Dossiers</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalDossiers}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                📁
              </div>
            </div>
          </div>

          {/* Dossiers Actifs */}
          <div className="rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 cursor-pointer bg-white hover:bg-gray-50"
               onClick={() => window.location.href = '/admin/dossiers'}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Dossiers Actifs</p>
                <p className="text-3xl font-bold text-gray-900">{stats.dossiersActifs}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                📈
              </div>
            </div>
          </div>

          {/* Clients */}
          <div className="rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 cursor-pointer bg-white hover:bg-gray-50"
               onClick={() => window.location.href = '/admin/users?filter=client'}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Clients</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalClients}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                👥
              </div>
            </div>
          </div>

          {/* ✅ MODIFIÉ : Dossiers en Retard avec nouveau système */}
          <div className="rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 cursor-pointer bg-white hover:bg-gray-50"
               onClick={() => window.location.href = '/admin/dossiers-retard'}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">En Retard</p>
                <p className="text-3xl font-bold text-gray-900">{stats.dossiersRetard}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                ⚠️
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Graphique Sources de Contact */}
          <div className="rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 bg-white">
            <div className="flex items-center mb-6">
              <div className="p-3 rounded-full mr-4 bg-blue-100">
                📊
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Sources de Contact</h3>
            </div>

            <div className="space-y-4">
              {sourcesContact.map((source, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: source.color }}
                    ></div>
                    <span className="font-medium text-gray-700">{source.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">{source.percentage}%</span>
                    <span className="font-bold text-gray-900">{source.value}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Graphique visuel */}
            <div className="mt-6 h-4 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full flex">
                {sourcesContact.map((source, index) => (
                  <div
                    key={index}
                    className="h-full transition-all duration-500 hover:opacity-80"
                    style={{ 
                      width: `${source.percentage}%`,
                      backgroundColor: source.color
                    }}
                    title={`${source.name}: ${source.value} (${source.percentage}%)`}
                  ></div>
                ))}
              </div>
            </div>
          </div>

          {/* Progression Globale */}
          <div className="rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 bg-white">
            <div className="flex items-center mb-6">
              <div className="p-3 rounded-full mr-4 bg-green-100">
                📊
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Performance Globale</h3>
            </div>

            <div className="text-center">
              <div className="text-6xl font-bold mb-4 text-gray-900">{stats.tauxProgression}%</div>
              <p className="text-lg text-gray-600">Progression moyenne des dossiers</p>
              
              {/* Barre de progression */}
              <div className="mt-6 w-full bg-gray-200 rounded-full h-4">
                <div 
                  className="bg-gradient-to-r from-green-400 to-green-600 h-4 rounded-full transition-all duration-1000"
                  style={{ width: `${stats.tauxProgression}%` }}
                ></div>
              </div>
            </div>

            {/* Actions rapides */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              <button 
                className="flex items-center justify-center p-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all"
                onClick={() => window.location.href = '/admin/create-dossier'}
              >
                ➕ Nouveau Dossier
              </button>
              <button 
                className="flex items-center justify-center p-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg hover:from-orange-600 hover:to-red-700 transition-all"
                onClick={() => window.location.href = '/admin/dossiers-retard'}
              >
                ⚠️ Dossiers Retard
              </button>
            </div>
          </div>
        </div>

        {/* Activité Récente */}
        <div className="rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 bg-white">
          <div className="flex items-center mb-6">
            <div className="p-3 rounded-full mr-4 bg-orange-100">
              📈
            </div>
            <h3 className="text-2xl font-bold text-gray-900">Activité Récente</h3>
          </div>

          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div 
                key={index} 
                className="flex items-center p-4 rounded-lg transition-colors cursor-pointer hover:bg-gray-50"
                onClick={() => window.location.href = `/admin/dossier/${activity.id}`}
              >
                <div className="mr-4">
                  {activity.status === 'completed' ? '✅' : 
                   activity.status === 'in-progress' ? '🔄' : '⏳'}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{activity.title}</h4>
                  <p className="text-sm text-gray-600">{activity.description}</p>
                </div>
                <div className="text-sm text-gray-500">
                  {activity.date.toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar complète */}
      <div className="w-64 bg-white border-r border-gray-200 shadow-lg">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Admin Super</h2>
          <p className="text-sm text-gray-600">{auth.currentUser?.email}</p>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          <Link to="/admin" className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-blue-500 text-white">
            <span>🏠</span>
            <span className="font-medium">Tableau de bord</span>
          </Link>
          <Link to="/admin/users" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100">
            <span>👥</span>
            <span className="font-medium">Utilisateurs</span>
          </Link>
          
          {/* ✅ NOUVEAU : Section Dossiers avec sous-menu */}
          <div className="space-y-1">
            <Link to="/admin/dossiers" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100">
              <span>📁</span>
              <span className="font-medium">Dossiers</span>
            </Link>
            
            {/* Sous-menus dossiers */}
            <div className="ml-8 space-y-1">
              <Link to="/admin/dossiers" className="flex items-center px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 text-sm">
                <span>📋 Tous les dossiers</span>
              </Link>
              {/* ✅ NOUVEAU : Lien vers dossiers en retard */}
              <Link to="/admin/dossiers-retard" className="flex items-center justify-between px-4 py-2 rounded-lg text-gray-600 hover:bg-red-50 text-sm">
                <span>⚠️ Dossiers en retard</span>
                {stats.dossiersRetard > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {stats.dossiersRetard}
                  </span>
                )}
              </Link>
              <Link to="/admin/dossiers-clotures" className="flex items-center px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 text-sm">
                <span>🗃️ Dossiers clôturés</span>
              </Link>
            </div>
          </div>

          <Link to="/admin/calendar" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100">
            <span>📅</span>
            <span className="font-medium">Calendrier</span>
          </Link>
          <Link to="/admin/sauvegarde" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100">
            <span>💾</span>
            <span className="font-medium">Sauvegarde</span>
          </Link>
          {/* Messagerie avec sous-menus - ✅ CORRIGÉ AVEC HOOK */}
          <div className="space-y-1">
            <Link to="/admin/messagerie" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 relative">
              <span>💬</span>
              <span className="font-medium">Messagerie</span>
              {messagesCount > 0 && (
                <span className="absolute top-1 right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {messagesCount > 99 ? '99+' : messagesCount}
                </span>
              )}
            </Link>
            
            {/* ✅ MODIFIÉ : Sous-menus indentés avec Discussion d'équipe et vignette rouge */}
            <div className="ml-8 space-y-1">
              <Link to="/admin/messagerie" className="flex items-center justify-between px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 text-sm">
                <span>Discussions en cours</span>
                {messagesCount > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {messagesCount > 99 ? '99+' : messagesCount}
                  </span>
                )}
              </Link>
              <Link to="/admin/messagerie/archivees" className="flex items-center px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 text-sm">
                <span>Discussions archivées</span>
              </Link>
              {/* ✅ NOUVEAU : Discussion d'équipe avec vignette rouge */}
              <Link to="/admin/discussion-equipe" className="flex items-center justify-between px-4 py-2 rounded-lg text-gray-600 hover:bg-blue-50 text-sm">
                <span>👥 Discussion d'équipe</span>
                {teamUnreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center animate-pulse">
                    {teamUnreadCount > 99 ? '99+' : teamUnreadCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
          {/* ✅ NOTIFICATIONS TIMELINE CORRIGÉE */}
          <Link to="/admin/notifications" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100">
            <span>🔔</span>
            <span className="font-medium">Notifications Timeline</span>
            {notificationsCount > 0 ? (
              <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {notificationsCount > 99 ? '99+' : notificationsCount}
              </span>
            ) : (
              <span className="text-green-500">✓</span>
            )}
          </Link>
          <Link to="/admin/settings" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100">
            <span>⚙️</span>
            <span className="font-medium">Paramètres</span>
          </Link>
        </nav>
      </div>
      
      {/* Contenu principal */}
      <div className="flex-1 p-8">
        {/* Affichage conditionnel : Dashboard sur /admin, autres pages via Outlet */}
        {(location.pathname === '/admin' || location.pathname === '/admin/') ? (
          renderDashboardContent()
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;